import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
  type PieLabelRenderProps,
} from 'recharts';
import { ResponsiveSankey } from '@nivo/sankey';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import api from '../../lib/api';
import { money } from '../../lib/money';
import {
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  IconButton,
  MetricCard,
  Money,
  Skeleton,
  Tag,
  ToolbarChip,
  ToolbarGroup,
  type ColumnDef,
} from '../ui';

/** Counts (not money) still format through Intl, never .toLocaleString. */
const NUM = new Intl.NumberFormat('en-US');

interface MonthlyCashFlow {
  month: string;
  income: number;
  expenses: number;
  net: number;
}

interface Category {
  category: string;
  total: number;
  count: number;
  percentage: number;
}

interface TopMerchant {
  merchant_name: string;
  merchant_category: string;
  total: number;
  count: number;
  is_waste: boolean;
}

interface IncomeSource {
  label: string;
  occurrences: number;
  avg_amount: number;
  total_amount: number;
  first_date: string | null;
  last_date: string | null;
  is_recurring: boolean;
}

interface CashFlowSummary {
  avg_monthly_income: number;
  avg_monthly_expenses: number;
  avg_monthly_net: number;
  total_income: number;
  total_expenses: number;
  months_covered: number;
  waste_total: number;
  waste_count: number;
}

interface CashFlowData {
  monthly: MonthlyCashFlow[];
  categories: Category[];
  top_merchants: TopMerchant[];
  income_sources: IncomeSource[];
  spouse_labels: string[];
  summary: CashFlowSummary;
}

/**
 * Chart palette — duplicated from index.css §13 CHARTS (--color-chart-1..8 +
 * pos/neg/neutral/grid/axis). Recharts and nivo need literal color strings
 * (nivo derives label colors via d3-color, which cannot resolve CSS var()
 * references), so the hexes live here in this ONE const. Keep in sync with
 * index.css.
 */
const CHART = {
  c1: '#44926F', // --color-chart-1
  c2: '#21909D', // --color-chart-2
  c3: '#5583B7', // --color-chart-3
  c4: '#8674B2', // --color-chart-4
  c5: '#A76990', // --color-chart-5
  c7: '#A3763B', // --color-chart-7
  c8: '#7C8842', // --color-chart-8
  pos: '#3B8966', // --color-chart-pos (green-600)
  neg: '#B26961', // --color-chart-neg (== chart-6, reserved for waste/expense)
  neutral: '#DBBB96', // --color-chart-neutral (clay-300)
  grid: '#EAE2D6', // --color-chart-grid (border-subtle)
  axis: '#6B655D', // --color-chart-axis (ink-faint)
  net: '#1C4331', // --color-green-900 — the net line
  onColor: '#FFFCF7', // --color-cream-light — labels on filled slices
} as const;

/** Categories map onto the 8-token chart wheel; every waste category shares
 *  chart-neg so "red slice = potential waste" reads consistently. */
const CATEGORY_COLORS: Record<string, string> = {
  Groceries: CHART.c1,
  Dining: CHART.c7,
  'Food Delivery': CHART.c5,
  Shopping: CHART.c4,
  Transportation: CHART.c3,
  'Gas/Auto': CHART.c2,
  'Medical/Health': CHART.c8,
  Home: CHART.c1,
  Utilities: CHART.c3,
  Insurance: CHART.c2,
  Subscription: CHART.c4,
  'Childcare/Education': CHART.c8,
  Travel: CHART.c2,
  Pet: CHART.c7,
  Fitness: CHART.c1,
  'ATM/Cash': CHART.neutral,
  'Alcohol/Bars/Nightlife': CHART.neg,
  'Gambling/Betting': CHART.neg,
  'Luxury/Designer': CHART.neg,
  'Jewelry/Watches': CHART.neg,
  'Adult Entertainment': CHART.neg,
  Other: CHART.neutral,
};

const WASTE_CATEGORIES = new Set([
  'Gambling/Betting',
  'Alcohol/Bars/Nightlife',
  'Luxury/Designer',
  'Jewelry/Watches',
  'Adult Entertainment',
]);

function getCategoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? CHART.neutral;
}

interface MerchantRow extends TopMerchant {
  rank: number;
  isWaste: boolean;
}

const MERCHANT_COLUMNS: ColumnDef<MerchantRow>[] = [
  {
    id: 'rank',
    header: '#',
    width: 'w-10',
    edge: true,
    cell: (r) => <span className="text-xs text-ink-faint">{r.rank}</span>,
  },
  {
    id: 'merchant',
    header: 'Merchant',
    width: 'flex',
    minWidth: 'min-w-48',
    cell: (r) => (
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: getCategoryColor(r.merchant_category) }}
        />
        <span className={`font-medium truncate ${r.isWaste ? 'text-danger' : 'text-ink'}`}>
          {r.merchant_name}
        </span>
        {r.isWaste && <AlertTriangle size={12} className="text-danger shrink-0" />}
      </div>
    ),
  },
  {
    id: 'category',
    header: 'Category',
    width: 'w-44',
    cell: (r) => <Tag size="sm" tone={r.isWaste ? 'danger' : 'neutral'}>{r.merchant_category}</Tag>,
  },
  {
    id: 'count',
    header: 'Transactions',
    width: 'w-28',
    numeric: true,
    cell: (r) => <span className="text-xs text-ink-faint">{NUM.format(r.count)}</span>,
  },
  {
    id: 'total',
    header: 'Total Spent',
    width: 'w-32',
    numeric: true,
    edge: true,
    cell: (r) => <Money value={r.total} className="font-semibold" />,
  },
];

// ─── Period picker helpers ──────────────────────────────────────────────────

type PeriodScope = 'all' | 'year' | 'quarter' | 'month';

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function periodToDateRange(scope: PeriodScope, period: string): { startDate: string; endDate: string } {
  if (scope === 'all' || !period) return { startDate: '', endDate: '' };
  if (scope === 'year') return { startDate: `${period}-01-01`, endDate: `${period}-12-31` };
  if (scope === 'quarter') {
    const [year, q] = period.split('-Q');
    const starts: Record<string, string> = { '1': '01-01', '2': '04-01', '3': '07-01', '4': '10-01' };
    const ends: Record<string, string>   = { '1': '03-31', '2': '06-30', '3': '09-30', '4': '12-31' };
    return { startDate: `${year}-${starts[q]}`, endDate: `${year}-${ends[q]}` };
  }
  if (scope === 'month') {
    const [year, month] = period.split('-');
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    return { startDate: `${period}-01`, endDate: `${period}-${String(lastDay).padStart(2, '0')}` };
  }
  return { startDate: '', endDate: '' };
}

function getAvailableYears(months: string[]): string[] {
  return [...new Set(months.map(m => m.slice(0, 4)))].sort();
}

function getAvailableQuarters(months: string[]): string[] {
  const qs = new Set(months.map(m => {
    const yr = m.slice(0, 4);
    const mo = parseInt(m.slice(5, 7));
    return `${yr}-Q${Math.ceil(mo / 3)}`;
  }));
  return [...qs].sort();
}


interface SankeyNode {
  id: string;
}

interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
  totalExpenses: number;
  surplus: number;
  totalIncome: number;
}

function buildSankeyData(categories: Category[], totalIncome: number): SankeyData | null {
  if (categories.length < 2 || totalIncome <= 0) return null;

  const totalExpenses = categories.reduce((a, c) => a + c.total, 0);
  const surplus = totalIncome - totalExpenses;

  const nodes: SankeyNode[] = [{ id: 'Income' }];
  const links: SankeyLink[] = [];

  for (const cat of categories) {
    if (cat.total > 0.01) {
      nodes.push({ id: cat.category });
      links.push({ source: 'Income', target: cat.category, value: Math.round(cat.total * 100) / 100 });
    }
  }

  if (surplus > 0.01) {
    nodes.push({ id: 'Savings / Surplus' });
    links.push({ source: 'Income', target: 'Savings / Surplus', value: Math.round(surplus * 100) / 100 });
  }

  if (links.length < 1) return null;

  return { nodes, links, totalExpenses, surplus, totalIncome };
}

function getSankeyNodeColor(nodeId: string): string {
  if (nodeId === 'Income') return CHART.pos;
  if (nodeId === 'Savings / Surplus') return CHART.neutral;
  if (WASTE_CATEGORIES.has(nodeId)) return CHART.neg;
  return getCategoryColor(nodeId);
}

function fmtAxis(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 10000) return `${sign}$${(abs / 1000).toFixed(0)}k`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return money(value, { whole: true });
}

function fmtMonth(month: string): string {
  const [yr, mo] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(mo, 10) - 1]} '${yr.slice(2)}`;
}

interface ChartPayloadEntry {
  dataKey: string;
  value: number;
}

interface PiePayloadEntry {
  payload: Category;
}


const CustomMonthTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: ChartPayloadEntry[];
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const income = payload.find((p) => p.dataKey === 'income')?.value ?? 0;
  const expenses = payload.find((p) => p.dataKey === 'expenses')?.value ?? 0;
  const net = income - expenses;
  return (
    <div className="bg-surface-card border border-border rounded-lg shadow-md p-3 text-sm">
      <p className="font-semibold text-ink-soft mb-2">{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between gap-6">
          <span className="text-green-700">Income</span>
          <span className="t-num">{money(income)}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-danger">Expenses</span>
          <span className="t-num">{money(expenses)}</span>
        </div>
        <div className={`flex justify-between gap-6 pt-1 border-t border-border-subtle font-semibold ${net >= 0 ? 'text-green-800' : 'text-danger'}`}>
          <span>Net</span>
          <span className="t-num">{money(net)}</span>
        </div>
      </div>
    </div>
  );
};

const CustomPieTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: PiePayloadEntry[];
}) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface-card border border-border rounded-lg shadow-md p-3 text-sm">
      <p className="font-semibold text-ink-soft">{d.category}</p>
      <p className="text-ink-soft">{money(d.total)} · {d.percentage.toFixed(1)}%</p>
      <p className="text-ink-faint text-xs">{NUM.format(d.count)} transactions</p>
    </div>
  );
};

const renderDonutLabel = ({ cx = 0, cy = 0, midAngle = 0, innerRadius = 0, outerRadius = 0, percent = 0 }: PieLabelRenderProps) => {
  const pct = percent * 100;
  if (pct < 4) return null;
  const RADIAN = Math.PI / 180;
  const ri = Number(innerRadius);
  const ro = Number(outerRadius);
  const radius = ri + (ro - ri) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill={CHART.onColor} textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${pct.toFixed(0)}%`}
    </text>
  );
};

export default function CashFlowTab({ caseId }: { caseId: string }) {
  const [spouseLabel, setSpouseLabel] = useState('all');
  const [periodScope, setPeriodScope] = useState<PeriodScope>('all');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [pickerYear, setPickerYear] = useState('');

  const { startDate, endDate } = useMemo(
    () => periodToDateRange(periodScope, selectedPeriod),
    [periodScope, selectedPeriod]
  );

  // All-time fetch — for period picker (no date filter; key never changes with dates)
  const { data: rangeData } = useQuery<CashFlowData>({
    queryKey: ['cashflow', caseId, spouseLabel, '', ''],
    queryFn: () =>
      api.get(`/cases/${caseId}/cashflow?spouse_label=${spouseLabel}`).then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  const availableMonths  = rangeData?.monthly?.map(m => m.month) ?? [];
  const availableYears   = getAvailableYears(availableMonths);
  const availableQuarters = getAvailableQuarters(availableMonths);

  const { data, isLoading, error, refetch } = useQuery<CashFlowData>({
    queryKey: ['cashflow', caseId, spouseLabel, startDate, endDate],
    queryFn: () => {
      const params = new URLSearchParams({ spouse_label: spouseLabel });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      return api.get(`/cases/${caseId}/cashflow?${params.toString()}`).then((r) => r.data);
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 pb-8">
        <Skeleton variant="rect" className="h-14 w-full rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} variant="rect" className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton variant="rect" className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card padding="none">
        <EmptyState
          icon={AlertTriangle}
          title="Failed to load cash flow data"
          action={
            <Button variant="secondary" size="lg" icon={RefreshCw} onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  const { summary, monthly, categories, top_merchants, income_sources, spouse_labels } = data;

  if (monthly.length === 0) {
    return (
      <Card padding="none">
        <EmptyState
          icon={TrendingUp}
          title="No transaction data yet"
          description="Upload bank statements to see cash flow analysis."
        />
      </Card>
    );
  }

  const allSpouses = ['all', ...spouse_labels];
  const topCategories = categories.slice(0, 10);
  const wasteCategories = categories.filter((c) => WASTE_CATEGORIES.has(c.category));
  const sankeyData = buildSankeyData(categories, summary.total_income);

  return (
    <div className="space-y-6 pb-8">

      {/* Period filter */}
      {(() => {
        const effectiveYear = pickerYear || (availableYears.length > 0 ? availableYears[availableYears.length - 1] : '');
        const yearIdx = availableYears.indexOf(effectiveYear);
        const visibleQuarters = availableQuarters.filter(q => q.startsWith(effectiveYear));
        const visibleMonths   = availableMonths.filter(m => m.startsWith(effectiveYear));

        const handleScope = (scope: PeriodScope) => {
          setPeriodScope(scope);
          setSelectedPeriod('');
          if ((scope === 'quarter' || scope === 'month') && availableYears.length > 0) {
            setPickerYear(availableYears[availableYears.length - 1]);
          }
        };

        return (
          <Card padding="none" className="overflow-hidden">

            {/* Segmented control row */}
            <div className="flex items-center gap-3 px-4 pt-3 pb-2">
              {/* Scope segmented control */}
              <ToolbarGroup>
                {(['all', 'year', 'quarter', 'month'] as PeriodScope[]).map(scope => (
                  <ToolbarChip
                    key={scope}
                    selected={periodScope === scope}
                    onClick={() => handleScope(scope)}
                  >
                    {scope === 'all' ? 'All Time' : scope.charAt(0).toUpperCase() + scope.slice(1)}
                  </ToolbarChip>
                ))}
              </ToolbarGroup>

              {/* Spouse filter — right side */}
              {spouse_labels.length > 1 && (
                <ToolbarGroup className="ml-auto">
                  {allSpouses.map(s => (
                    <ToolbarChip
                      key={s}
                      selected={spouseLabel === s}
                      onClick={() => setSpouseLabel(s)}
                    >
                      {s === 'all' ? 'All' : s}
                    </ToolbarChip>
                  ))}
                </ToolbarGroup>
              )}
            </div>

            {/* Year chips */}
            {periodScope === 'year' && availableYears.length > 0 && (
              <div className="flex gap-2 px-4 pb-3">
                {availableYears.map(yr => (
                  <ToolbarChip
                    key={yr}
                    selected={selectedPeriod === yr}
                    onClick={() => setSelectedPeriod(selectedPeriod === yr ? '' : yr)}
                  >
                    {yr}
                  </ToolbarChip>
                ))}
              </div>
            )}

            {/* Quarter or Month — year navigator + grid */}
            {(periodScope === 'quarter' || periodScope === 'month') && effectiveYear && (
              <div className="px-4 pb-3 space-y-2">
                {/* Year navigator */}
                <div className="flex items-center gap-2">
                  <IconButton
                    icon={ChevronLeft}
                    size="sm"
                    variant="ghost"
                    aria-label="Previous year"
                    onClick={() => setPickerYear(availableYears[yearIdx - 1])}
                    disabled={yearIdx <= 0}
                  />
                  <span className="text-sm font-semibold text-ink-soft w-10 text-center">{effectiveYear}</span>
                  <IconButton
                    icon={ChevronRight}
                    size="sm"
                    variant="ghost"
                    aria-label="Next year"
                    onClick={() => setPickerYear(availableYears[yearIdx + 1])}
                    disabled={yearIdx >= availableYears.length - 1}
                  />
                </div>

                {/* Q1–Q4 grid */}
                {periodScope === 'quarter' && (
                  <div className="flex gap-2">
                    {['Q1','Q2','Q3','Q4'].map(q => {
                      const period = `${effectiveYear}-${q}`;
                      const avail  = visibleQuarters.includes(period);
                      return (
                        <ToolbarChip
                          key={q}
                          selected={selectedPeriod === period}
                          disabled={!avail}
                          onClick={() => avail && setSelectedPeriod(selectedPeriod === period ? '' : period)}
                          className="flex-1 justify-center"
                        >
                          {q}
                        </ToolbarChip>
                      );
                    })}
                  </div>
                )}

                {/* 6×2 month grid */}
                {periodScope === 'month' && (
                  <div className="grid grid-cols-6 gap-2">
                    {MONTH_LABELS.map((label, i) => {
                      const period = `${effectiveYear}-${String(i + 1).padStart(2, '0')}`;
                      const avail  = visibleMonths.includes(period);
                      return (
                        <ToolbarChip
                          key={period}
                          selected={selectedPeriod === period}
                          disabled={!avail}
                          onClick={() => avail && setSelectedPeriod(selectedPeriod === period ? '' : period)}
                          className="w-full justify-center px-0"
                        >
                          {label}
                        </ToolbarChip>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </Card>
        );
      })()}

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Avg Monthly Income"
          value={money(summary.avg_monthly_income, { whole: true })}
          subtitle={`${summary.months_covered} months`}
          icon={<ArrowUpRight size={20} />}
        />
        <MetricCard
          label="Avg Monthly Expenses"
          value={money(summary.avg_monthly_expenses, { whole: true })}
          subtitle={`Total: ${money(summary.total_expenses, { whole: true })}`}
          icon={<ArrowDownRight size={20} />}
        />
        <MetricCard
          label="Avg Net Cash Flow"
          value={
            <span className={summary.avg_monthly_net < 0 ? 'text-danger' : undefined}>
              {money(Math.abs(summary.avg_monthly_net), { whole: true })}
            </span>
          }
          subtitle={summary.avg_monthly_net >= 0 ? 'surplus / month' : 'deficit / month'}
          icon={summary.avg_monthly_net >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
        />
        <MetricCard
          label="Potential Waste"
          value={
            <span className={summary.waste_total > 0 ? 'text-warn' : undefined}>
              {money(summary.waste_total, { whole: true })}
            </span>
          }
          subtitle={`${summary.waste_count} flagged transaction${summary.waste_count !== 1 ? 's' : ''}`}
          icon={<AlertTriangle size={20} />}
        />
      </div>

      {/* Monthly Cash Flow Chart */}
      <Card>
        <CardHeader
          title="Monthly Cash Flow"
          subtitle="Income vs. expenses, excluding inter-account transfers"
          actions={
            <div className="flex items-center gap-4 text-xs text-ink-faint">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: CHART.pos }} />Income</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: CHART.neg }} />Expenses</span>
              <span className="flex items-center gap-1"><span className="w-5 h-px bg-green-900 inline-block" />Net</span>
            </div>
          }
        />
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={monthly} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
            <XAxis
              dataKey="month"
              tickFormatter={fmtMonth}
              tick={{ fontSize: 11, fill: CHART.axis }}
              interval={monthly.length > 18 ? Math.floor(monthly.length / 12) : 0}
            />
            <YAxis
              tickFormatter={fmtAxis}
              tick={{ fontSize: 11, fill: CHART.axis }}
              width={52}
            />
            <Tooltip content={<CustomMonthTooltip />} />
            <ReferenceLine y={0} stroke={CHART.axis} strokeWidth={1} />
            <Bar dataKey="income" fill={CHART.pos} radius={[3, 3, 0, 0]} maxBarSize={32} name="Income" />
            <Bar dataKey="expenses" fill={CHART.neg} radius={[3, 3, 0, 0]} maxBarSize={32} name="Expenses" />
            <Line
              type="monotone"
              dataKey="net"
              stroke={CHART.net}
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 3"
              name="Net"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Money Flow — Sankey diagram */}
      {sankeyData ? (
        <Card>
          <CardHeader
            title="Money Flow"
            subtitle={
              <>
                How income distributes across spending categories
                {sankeyData.surplus <= 0 && summary.total_income > 0 && summary.total_expenses > summary.total_income && (
                  <span className="ml-2 text-warn font-medium">· spending exceeds income</span>
                )}
              </>
            }
            actions={
              <div className="flex items-center gap-3 text-xs text-ink-faint">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: CHART.pos }} />
                  Income
                </span>
                {sankeyData.surplus > 0.01 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: CHART.neutral }} />
                    Surplus
                  </span>
                )}
                {wasteCategories.length > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: CHART.neg }} />
                    Potential Waste
                  </span>
                )}
              </div>
            }
          />
          <div style={{ height: 520 }}>
            <ResponsiveSankey
              data={sankeyData}
              margin={{ top: 16, right: 180, bottom: 16, left: 10 }}
              align="start"
              colors={(node) => getSankeyNodeColor(String(node.id))}
              nodeOpacity={0.92}
              nodeHoverOpacity={1}
              nodeThickness={18}
              nodeInnerPadding={3}
              nodeSpacing={16}
              nodeBorderRadius={3}
              nodeBorderWidth={0}
              linkOpacity={0.35}
              linkHoverOpacity={0.7}
              linkHoverOthersOpacity={0.1}
              linkContract={1}
              enableLinkGradient={true}
              labelPosition="outside"
              labelOrientation="horizontal"
              labelPadding={14}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.4]] }}
              nodeTooltip={({ node }) => (
                <div className="bg-surface-card border border-border rounded-lg shadow-md p-3 text-sm pointer-events-none">
                  <p className="font-semibold text-ink-soft">{String(node.id)}</p>
                  <p className="text-ink font-medium">{money(node.value)}</p>
                  {String(node.id) !== 'Income' && sankeyData.totalIncome > 0 && (
                    <p className="text-ink-faint text-xs mt-1">
                      {((node.value / sankeyData.totalIncome) * 100).toFixed(1)}% of income
                    </p>
                  )}
                </div>
              )}
              linkTooltip={({ link }) => (
                <div className="bg-surface-card border border-border rounded-lg shadow-md p-3 text-sm pointer-events-none">
                  <p className="font-semibold text-ink-soft">
                    {String(link.source.id)} → {String(link.target.id)}
                  </p>
                  <p className="text-ink font-medium">{money(link.value)}</p>
                  {sankeyData.totalIncome > 0 && (
                    <p className="text-ink-faint text-xs mt-1">
                      {((link.value / sankeyData.totalIncome) * 100).toFixed(1)}% of income
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader title="Money Flow" />
          <p className="text-ink-faint text-sm text-center py-12">
            {categories.length < 2
              ? 'Needs at least two spending categories to display the flow diagram.'
              : 'No income found in the selected period — upload statements with income transactions.'}
          </p>
        </Card>
      )}

      {/* Middle row: Category Donut + Income Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Spending by Category */}
        <Card>
          <CardHeader
            title="Spending by Category"
            subtitle="Breakdown of all expense transactions"
          />
          {topCategories.length === 0 ? (
            <p className="text-ink-faint text-sm text-center py-8">No expense data</p>
          ) : (
            <div className="flex flex-col gap-4">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={topCategories}
                    dataKey="total"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    labelLine={false}
                    label={renderDonutLabel}
                  >
                    {topCategories.map((entry) => (
                      <Cell key={entry.category} fill={getCategoryColor(entry.category)} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-2">
                {topCategories.map((cat) => (
                  <div key={cat.category} className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getCategoryColor(cat.category) }}
                    />
                    <span className={`text-xs flex-1 ${WASTE_CATEGORIES.has(cat.category) ? 'text-danger font-medium' : 'text-ink-soft'}`}>
                      {cat.category}
                      {WASTE_CATEGORIES.has(cat.category) && (
                        <AlertTriangle size={12} className="ml-1 text-danger inline" />
                      )}
                    </span>
                    <span className="text-xs text-ink-faint">{cat.percentage.toFixed(1)}%</span>
                    <span className="w-20 text-right"><Money value={cat.total} whole className="text-xs" /></span>
                  </div>
                ))}
              </div>

              {wasteCategories.length > 0 && (
                <div className="mt-2 p-3 bg-warn-soft border border-warn-accent rounded-lg">
                  <p className="text-xs font-semibold text-warn mb-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> Potential Waste Categories
                  </p>
                  <p className="text-xs text-warn">
                    {wasteCategories.map((c) => c.category).join(', ')} — {money(wasteCategories.reduce((a, c) => a + c.total, 0), { whole: true })} total
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Income Analysis */}
        <Card>
          <CardHeader
            title="Income Analysis"
            subtitle="Recurring deposit sources (relevant for support calculations)"
          />
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-green-100 rounded-lg p-3">
              <p className="text-xs text-green-700 font-medium">Avg Monthly Income</p>
              <p className="t-num-hero text-green-900 mt-1">{money(summary.avg_monthly_income, { whole: true })}</p>
            </div>
            <div className="bg-green-100 rounded-lg p-3">
              <p className="text-xs text-green-700 font-medium">Total Income</p>
              <p className="t-num-hero text-green-900 mt-1">{money(summary.total_income, { whole: true })}</p>
              <p className="text-xs text-green-700 mt-1">{summary.months_covered} months</p>
            </div>
          </div>

          {income_sources.length === 0 ? (
            <p className="text-ink-faint text-sm text-center py-4">No recurring income sources found</p>
          ) : (
            <div>
              <p className="t-eyebrow mb-2">Recurring Sources</p>
              <div className="space-y-2">
                {income_sources.map((src, i) => (
                  <div key={i} className="flex items-start justify-between gap-2 py-2 border-b border-border-subtle last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {src.is_recurring && (
                          <span className="w-2 h-2 rounded-full bg-good flex-shrink-0" />
                        )}
                        <p className="text-xs font-medium text-ink truncate">{src.label}</p>
                      </div>
                      <p className="text-xs text-ink-faint mt-1">
                        {src.occurrences}× · avg {money(src.avg_amount)}/occurrence
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-green-800">{money(src.avg_amount, { whole: true })}</p>
                      <p className="text-xs text-ink-faint">avg/time</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-ink-faint mt-3 italic">
                Green dot = appeared 3+ times. Useful for establishing regular income for support calculations.
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Top Merchants */}
      <div>
        <div className="mb-3">
          <h3 className="t-card text-green-900">Top Merchants by Spend</h3>
          <p className="text-xs text-ink-faint mt-1">Where money went most — sorted by total amount</p>
        </div>
        <DataTable
          caption="Top merchants by total spend"
          rows={top_merchants.map((m, i): MerchantRow => ({
            ...m,
            rank: i + 1,
            isWaste: m.is_waste || WASTE_CATEGORIES.has(m.merchant_category),
          }))}
          columns={MERCHANT_COLUMNS}
          rowKey={(r) => r.rank}
          minWidth={640}
          stickyHeader={false}
          rowTone={(r) => (r.isWaste ? 'flagged' : 'default')}
          empty={<p className="text-ink-faint text-sm text-center py-8">No merchant data available</p>}
        />
      </div>

      {/* Spending vs Income ratio insight */}
      {summary.months_covered > 0 && (
        <div className={`rounded-lg border p-4 flex items-start gap-3 ${summary.avg_monthly_net >= 0 ? 'bg-green-100 border-green-200' : 'bg-danger-soft border-danger'}`}>
          <div className={summary.avg_monthly_net >= 0 ? 'text-green-700' : 'text-danger'}>
            {summary.avg_monthly_net >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
          </div>
          <div>
            <p className={`text-sm font-semibold ${summary.avg_monthly_net >= 0 ? 'text-green-800' : 'text-danger'}`}>
              {summary.avg_monthly_net >= 0
                ? `Lifestyle is within income — averaging ${money(summary.avg_monthly_net, { whole: true })}/month surplus`
                : `Spending exceeds income by ${money(Math.abs(summary.avg_monthly_net), { whole: true })}/month on average`}
            </p>
            <p className="text-xs text-ink-faint mt-1">
              Spending rate: {summary.avg_monthly_income > 0 ? Math.round((summary.avg_monthly_expenses / summary.avg_monthly_income) * 100) : 0}% of income.
              {summary.waste_total > 0 && ` ${money(summary.waste_total, { whole: true })} in potential waste identified.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
